import {useUserTypeContext} from '@/contexts/UserTypeContext/UserTypeContext';
import {useFeatures} from '@/hooks/useFeatures/useFeatures';
import {useIsMobile} from '@ark/web';
import {useLogAppUse} from '@/hooks/useLogAppUse';
import {LayoutSwitch} from '@/layout/LayoutSwitch';
import {SentryRoutes} from '@/main';
import React, {useEffect} from 'react';
import lazyWithPreload from 'react-lazy-with-preload';
import {Route} from 'react-router-dom';
import {importWithRetry, preloadAll} from '@/utils/chunkReloadOperations';

const ContactPage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/ENT/Contacts/Detail/ContactDetailsPage').then(
      module => ({
        default: module.ContactDetailsPage,
      }),
    ),
  ),
);
const ContactsListPage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/ENT/Contacts/List/ContactsListPage').then(module => ({
      default: module.ContactsListPage,
    })),
  ),
);
const PackagesPage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/ENT/Packages/List/PackagesPage').then(module => ({
      default: module.PackagesPage,
    })),
  ),
);
const KeysListPage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/ENT/Keys/List/KeysListPage').then(module => ({
      default: module.KeysListPage,
    })),
  ),
);
const CommunityProductsPage = lazyWithPreload(() =>
  importWithRetry(() =>
    import(
      '../../pages/ENT/CommunityProducts/List/CommunityProductsListPage'
    ).then(module => ({
      default: module.CommunityProductsListPage,
    })),
  ),
);
const HomePage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/ENT/Homes/Detail/HomeDetailsPage').then(module => ({
      default: module.HomeDetailsPage,
    })),
  ),
);
const HomesListPage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/ENT/Homes/List/HomesListPage').then(module => ({
      default: module.HomesListPage,
    })),
  ),
);
const ProfilePage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/ENT/ProfilePage/ProfilePage').then(module => ({
      default: module.ProfilePage,
    })),
  ),
);
const PageNotFound = lazyWithPreload(() =>
  importWithRetry(() => import('../../pages/ENT/404Page')),
);
const AmenitiesListPage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/ENT/Amenities/List/AmenitiesListPage').then(module => ({
      default: module.AmenitiesListPage,
    })),
  ),
);
const AppointmentsListPage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/ENT/Appointments/List/AppointmentsListPage').then(
      module => ({
        default: module.AppointmentsListPage,
      }),
    ),
  ),
);
const ConversationsListPage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/ENT/Conversations/List/ConversationsPage').then(
      module => ({
        default: module.ConversationsPage,
      }),
    ),
  ),
);
const ConversationsPageV2Style = lazyWithPreload(() =>
  importWithRetry(() =>
    import(
      '../../pages/ENT/Conversations/ConversationPageV2Style/ConversationsPage'
    ).then(module => ({
      default: module.ConversationsPage,
    })),
  ),
);
const VisitorsListPage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/ENT/Visitors/List/VisitorsListPage').then(module => ({
      default: module.VisitorsListPage,
    })),
  ),
);
const ContractorsListPage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/ENT/Contractors/List/ContractorsListPage').then(
      module => ({
        default: module.ContractorsListPage,
      }),
    ),
  ),
);
const HomesPackagesPage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/ENT/Packages/Detail/HomePackagesPage').then(module => ({
      default: module.HomePackagesPage,
    })),
  ),
);

const NewsFeedPage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/ENT/Updates/List/NewsFeedPage').then(module => ({
      default: module.NewsFeedPage,
    })),
  ),
);
const EventsListPage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/ENT/Events/List/EventsListPage').then(module => ({
      default: module.EventsListPage,
    })),
  ),
);

const MarketplaceListPage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/ENT/Marketplace/List/MarketplaceListPage').then(
      module => ({
        default: module.MarketplaceListPage,
      }),
    ),
  ),
);

const NotificationListPage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/ENT/Notifications/List/NotificationListPage').then(
      module => ({
        default: module.NotificationListPage,
      }),
    ),
  ),
);

const PulsePage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/ENT/Pulse/List/PulsePage').then(module => ({
      default: module.PulsePage,
    })),
  ),
);

const FeaturesPage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/ENT/Features/List/FeaturesPage').then(module => ({
      default: module.FeaturesPage,
    })),
  ),
);

const TasksPage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/ENT/Tasks/List/TasksPage').then(module => ({
      default: module.TasksPage,
    })),
  ),
);

const ViewTaskPage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/ENT/Tasks/Tabs/ViewTaskPage').then(module => ({
      default: module.ViewTaskPage,
    })),
  ),
);

const BlocksListPage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/ENT/Blocks/List/BlockListPage').then(module => ({
      default: module.BlocksListPage,
    })),
  ),
);

const componentsToPreload = [
  ContactPage,
  ContactsListPage,
  PackagesPage,
  KeysListPage,
  CommunityProductsPage,
  HomePage,
  HomesListPage,
  ProfilePage,
  PageNotFound,
  EventsListPage,
  AmenitiesListPage,
  AppointmentsListPage,
  ConversationsListPage,
  ConversationsPageV2Style,
  VisitorsListPage,
  HomesPackagesPage,
  NewsFeedPage,
  MarketplaceListPage,
  NotificationListPage,
  PulsePage,
  FeaturesPage,
  TasksPage,
  ViewTaskPage,
  ContractorsListPage,
  BlocksListPage,
];

const ConciergeRoot: React.FC = () => {
  useLogAppUse();
  const [features] = useFeatures();
  const isMobile = useIsMobile('md');
  const {hasBlocks} = useUserTypeContext();
  useEffect(() => {
    preloadAll(componentsToPreload);
  }, []);

  return (
    <SentryRoutes>
      <Route element={<LayoutSwitch />}>
        <Route path="/" element={<PackagesPage />} />
        <Route path="/events/:id?/:action?" element={<EventsListPage />} />
        <Route
          path="/updates/:id?/:action?/:communityId?"
          element={<NewsFeedPage />}
        />
        <Route
          path="/contact-profiles/:contactId?/:dialogType?/:dialogId?/:action?"
          element={<ContactPage />}
        />
        <Route path="/contact-profiles/" element={<ContactsListPage />} />
        <Route path="/packages/:packageId?" element={<PackagesPage />} />
        <Route
          path="/keys/:keyId?/:action?/:keyholderId?/:action?"
          element={<KeysListPage />}
        />
        <Route
          path="/community-products/:id?"
          element={<CommunityProductsPage />}
        />
        <Route
          path="/homes/:homeId?/packages/:packageId?"
          element={<HomesPackagesPage />}
        />
        <Route
          path="/homes/:homeId/group?/:groupId?/:action?"
          element={<HomePage />}
        />
        <Route path="/homes" element={<HomesListPage />} />
        <Route path="/profile/:tab?" element={<ProfilePage />} />
        <Route
          path="/facilities/:id?/:action?"
          element={<AmenitiesListPage />}
        />
        <Route
          path="/appointments/:id?/:action?"
          element={<AppointmentsListPage />}
        />
        <Route
          path="/messages/:conversationId?"
          element={
            features.ConversationLayout && !isMobile ? (
              <ConversationsListPage />
            ) : (
              <ConversationsPageV2Style />
            )
          }
        />
        <Route
          path="/visitors/qr-codes/:communityId?"
          element={<VisitorsListPage />}
        />
        <Route path="/visitors/:id?" element={<VisitorsListPage />} />
        {features.Contractors && (
          <Route
            path="/contractors/:contractorCompanyId?"
            element={<ContractorsListPage />}
          />
        )}
        <Route path="/marketplace/:id?" element={<MarketplaceListPage />} />
        <Route path="/notifications" element={<NotificationListPage />} />
        <Route path="/pulse" element={<PulsePage />} />
        <Route path="/features/:slug?" element={<FeaturesPage />} />
        {features.Tasks && (
          <>
            <Route path="/tasks" element={<TasksPage />} />
            <Route path="/tasks/:taskId/:tab?" element={<ViewTaskPage />} />
          </>
        )}
        {features.AutomatedBlocks && hasBlocks && (
          <>
            <Route path="/blocks" element={<BlocksListPage />} />
          </>
        )}
        <Route path="*" element={<PageNotFound />} />
      </Route>
    </SentryRoutes>
  );
};

export default ConciergeRoot;
